import React, { FC, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Money } from 'shared/types/product';
import FreeDelivery from 'components/commercetools-ui/atoms/fee-delivery';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useCart } from 'frontastic';
import AppliedGiftcards from './appliedGiftCards';
import useCostsData from '../hooks/useCostsData';
import { CostsProps } from '../types';
import Link from 'components/commercetools-ui/atoms/link';

const Costs: FC<CostsProps> = ({
  className,
  order,
  dataReference = 'cart',
  subCostsContainerClassName,
  subCostClassName,
  totalAmountClassName,
  isFreeDeliveryBannerRequired = true,
}) => {
  const { ctLocale: locale } = useLocale();
  const [cookies, _] = useCookies(['locale_selection']);
  const [currency, setCurrency] = useState('GBP');
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { loading, costsToRender, total } = useCostsData({ dataReference, order });
  const { taxCategoryRate, estimatedShippingPrice, data } = useCart();

  const isOrderData = dataReference == 'order' ? true : false;
  const hasGiftcard = data?.payments?.find((p) => p.giftcardNumber != undefined);
  const hasTaxed = data?.taxed ? true : false;
  const hasShippingInfo = data?.shippingInfo ? true : false;
  const isStoreCollection = data?.custom?.deliveryOption == 'StoreCollection' ? true : false;

  const isListEmpty = isOrderData
    ? order?.lineItems && order?.lineItems.length > 0
      ? false
      : true
    : data?.lineItems && data?.lineItems.length > 0
    ? false
    : true;

  const showExpectedDelivery = isOrderData
    ? false
    : !hasShippingInfo && !isListEmpty && !isStoreCollection
    ? true
    : false;
  const isAllGiftcards = data?.lineItems?.every((item) => item.isGiftcard === true) ?? false;

  const showExpectedTaxDetails = isOrderData ? false : !hasTaxed && !isAllGiftcards && !isListEmpty ? true : false;

  const showEstimatedTotal = isOrderData
    ? false
    : !hasShippingInfo && !isListEmpty && !isStoreCollection
    ? true
    : false;

  const estimatedTaxAmount = useMemo(() => {
    let totalGiftcardPrice = 0;

    data?.lineItems
      ?.filter((item) => item.isGiftcard)
      .forEach((item) => {
        totalGiftcardPrice += item.totalPrice?.centAmount ?? 0;
      });

    const taxedAmount: number = (total?.value?.centAmount ?? 0) - totalGiftcardPrice || 0;

    const taxDecimal: number = taxCategoryRate?.data?.amount || 0;
    const originalAmount = taxedAmount / (1 + taxDecimal);
    const taxAsMoney: Money = {
      fractionDigits: 2,
      centAmount: taxedAmount - originalAmount,
      currencyCode: currency,
    };
    return taxAsMoney;
  }, [data?.taxed, total, taxCategoryRate?.data]);

  const estimatedTotalAmount: Money = useMemo(() => {
    const totalAmount: number = total?.value?.centAmount || 0;
    const estShppingCost: number = estimatedShippingPrice?.data?.centAmount || 0;
    const estimatedTotalAmount = totalAmount + estShppingCost;
    const estimatedTotalAsMoney: Money = {
      fractionDigits: 2,
      centAmount: estimatedTotalAmount,
      currencyCode: currency,
    };
    return estimatedTotalAsMoney;
  }, [data?.taxed, total, estimatedShippingPrice?.data]);

  const totalAmountClassNames = useClassNames([
    'mt-24 flex items-center justify-between font-medium',
    totalAmountClassName,
  ]);

  const subCostsContainerClassNames = useClassNames(['grid gap-8', subCostsContainerClassName]);
  const subCostsClassNames = useClassNames([
    'flex items-center justify-between capitalize text-secondary-black text-13 font-light',
    subCostClassName,
  ]);

  useEffect(() => {
    if (cookies?.locale_selection) setCurrency(cookies?.locale_selection['currency']);
  }, [cookies]);

  return (
    <div className={className}>
      <div className={subCostsContainerClassNames}>
        {costsToRender.map(({ key, label, value }) =>
          (key == 'shipping' && !showExpectedDelivery && !isListEmpty) ||
          (value?.centAmount && value.centAmount > 0) ? (
            <div key={key} className={subCostsClassNames}>
              <Typography asSkeleton={loading} className="font-light">
                {label}
              </Typography>
              <Typography asSkeleton={loading} className="font-medium">
                {CurrencyHelpers.formatForCurrency(value, locale)}
              </Typography>
            </div>
          ) : (
            <span className="hidden" key={key}></span>
          ),
        )}
        {showExpectedDelivery && (
          <div key="shipping-cost" className={subCostsClassNames}>
            <div className="font-light">
              {formatCartMessage({ id: 'estimateed.delivery.cost', defaultMessage: 'Est. Delivery cost' })}
            </div>
            <div className="font-medium">{CurrencyHelpers.formatForCurrency(estimatedShippingPrice?.data, locale)}</div>
          </div>
        )}
        {showExpectedTaxDetails && (
          <div key="tax" className={subCostsClassNames}>
            <div className="font-light">
              {formatCartMessage({ id: 'vat.sales.tax', defaultMessage: 'VAT / Sales tax' })}{' '}
              {taxCategoryRate?.data?.name}
            </div>
            <div className="font-medium">{CurrencyHelpers.formatForCurrency(estimatedTaxAmount, locale)}</div>
          </div>
        )}
      </div>
      {hasGiftcard && !isOrderData && (
        <AppliedGiftcards
          subCostsContainerClassNames={subCostsContainerClassNames}
          subCostsClassNames={subCostsClassNames}
          section="cost"
        ></AppliedGiftcards>
      )}
      <div className={totalAmountClassNames}>
        <Typography asSkeleton={loading} className="text-13 font-medium normal-case">
          {total.label}
          {/* {'Order Subtotal'} */}
        </Typography>
        {showEstimatedTotal && (
          <Typography asSkeleton={loading} className="text-23 font-bold normal-case">
            {CurrencyHelpers.formatForCurrency(estimatedTotalAmount, locale)}
          </Typography>
        )}
        {!showEstimatedTotal && (
          <Typography asSkeleton={loading} className="text-23 font-bold normal-case">
            {CurrencyHelpers.formatForCurrency(total.value, locale)}
          </Typography>
        )}
      </div>

      {isFreeDeliveryBannerRequired && showExpectedDelivery && estimatedShippingPrice?.data?.centAmount == 0 && (
        <FreeDelivery></FreeDelivery>
      )}
    </div>
  );
};

export default Costs;
